import {
  Apple,
  ArrowRight,
  BarChart3,
  CalendarPlus,
  MessageSquareText,
  PenLine,
  Rows3,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { usePublicAnalytics } from "@/hooks/usePublicAnalytics";

interface CoachCapability {
  key: string;
  icon: LucideIcon;
}

const COACH_CAPABILITIES: CoachCapability[] = [
  { key: "question", icon: MessageSquareText },
  { key: "session", icon: CalendarPlus },
  { key: "edit", icon: PenLine },
  { key: "nutrition", icon: Apple },
  { key: "progress", icon: BarChart3 },
  { key: "plan", icon: Rows3 },
];

export function CoachProof() {
  const { t } = useTranslation();
  const { track } = usePublicAnalytics();

  return (
    <section id="coach-proof" className="dark bg-[#101211] px-4 py-20 text-white md:px-8 md:py-28">
      <div className="mx-auto grid max-w-[1280px] gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="text-sm font-semibold uppercase text-white/55">
            {t("frontpage.precision.coach.eyebrow")}
          </p>
          <h2 className="mt-2 max-w-[13ch] font-display text-[36px] font-bold leading-[1.05] md:text-[52px]">
            {t("frontpage.precision.coach.title")}
          </h2>
          <p className="mt-5 max-w-[48ch] text-base leading-7 text-white/70">
            {t("frontpage.precision.coach.body")}
          </p>

          <div className="mt-8 border-l-4 border-brand pl-4">
            <p className="text-sm font-semibold text-white">
              {t("frontpage.precision.coach.free_allowance")}
            </p>
            <p className="mt-1 text-sm leading-6 text-white/60">
              {t("frontpage.precision.coach.credit_note")}
            </p>
          </div>

          <Link
            to={ROUTES.SIGNUP}
            onClick={() => void track("public_cta_clicked", "coach_proof")}
            className="mt-8 inline-flex min-h-12 items-center justify-center gap-2 bg-brand px-6 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#101211]"
          >
            {t("frontpage.precision.coach.cta")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        <div className="border border-white/20" aria-label={t("frontpage.precision.coach.list_label")}>
          <div className="border-b border-white/20 px-5 py-4 text-xs font-semibold uppercase text-white/45 md:px-6">
            <span>{t("frontpage.precision.coach.action_heading")}</span>
          </div>
          {COACH_CAPABILITIES.map(({ key, icon: Icon }) => (
            <div
              key={key}
              className="grid min-h-[104px] grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 border-b border-white/15 px-5 py-5 last:border-b-0 sm:grid-cols-[auto_1fr_auto] md:px-6"
            >
              <Icon className="h-5 w-5 text-brand" aria-hidden />
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-white">
                  {t(`frontpage.precision.coach.capabilities.${key}.title`)}
                </h3>
                <p className="mt-1 max-w-[56ch] text-sm leading-6 text-white/60">
                  {t(`frontpage.precision.coach.capabilities.${key}.body`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
